import inlineCTA from "assets/images/psp/inlineCTA.jpg";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import FemaleAvatar from "assets/images/psp/femaleavatar.jpg";
import prepareMotorcycle from "assets/images/psp/prepare-motorbike-for-storage.webp";
import motorCycleStorage from "assets/images/psp/motorcycle-storage.webp";
import motorCycleWinterize from "assets/images/psp/motorcycle-winterize.webp";
import garageVsStorage from "assets/images/psp/garage-vs-storage.webp";
import HeaderBackgroundDesktop from "assets/images/psp/hero/motorcycle-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/motorcycle-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/motorcycle-header-tablet.webp";
import { storageCategories } from "../utils/getPSPLinks";

const prepareMotorcycleRichContent = `
**Essential Pre-Storage Checklist**

**1. Mechanical Preparation:**
* Change oil and filter (fresh oil prevents acid buildup)
* Fill the gas tank and add fuel stabilizer
* Check and inflate tires to the recommended pressure
* Clean and lubricate the chain (if applicable)
* Remove the battery or connect to the maintenance charger

**2. Cleaning & Protection:**
* Wash and thoroughly dry the motorcycle
* Apply protective wax to painted surfaces
* Clean and condition leather components
* Remove any food items that might attract pests

**3. Storage Positioning:**

Can you store a motorcycle in self-storage? Absolutely, and proper positioning is key:
* Center stand preferred: Keeps both wheels off the ground
* Cover with breathable material: Prevents dust while allowing air circulation
* Avoid kickstand only: Can cause flat spots on tires over time
* Position away from the unit door: Prevents accidental damage during access

**Motorcycle Storage Unit Setup Tips**

**Organization Ideas:**
* Install wall hooks for helmets and riding gear
* Use clear storage bins for maintenance supplies and tools
* Create an inventory checklist for easy seasonal prep
* Designate specific areas for different types of gear

**Protection Strategies:**
* Use motorcycle covers designed for storage (breathable materials)
* Place moisture absorbers in humid climates
* Position the bike on the center stand or rear stand when possible
* Keep the unit organized to prevent accidental damage when accessing items
`;

const optionsRichContent = `
### Indoor Storage Units for Motorcycles

**Best for**: Premium bikes, year-round storage, harsh climates 

* Complete weather protection
* Climate control available
* Maximum security
* Higher monthly cost, but provides the best protection of all motorcycle storage options

### Covered Motorcycle Storage
**Best for**: Budget-conscious riders, moderate climates
* Protection from rain and sun
* More affordable than indoor units
* Adequate theft protection
* Limited temperature control

### Outdoor Motorcycle Storage
**Best for**: Short-term storage, mild climates, budget storage, riders with temporary at-home space limitations
* Most economical option
* Requires a quality motorcycle cover
* Basic security features
* Limited weather protection
`;
const faqs = [
	{
		question: "What size storage unit do I need for my motorcycle?",
		answer: "Most motorcycles need a 5x10 unit for basic storage. Sport bikes like the Yamaha R6 and cruisers like the Harley Sportster fit comfortably in this size with room for gear. Larger touring bikes such as the Honda Gold Wing require 5x15 or 10x10 units.\n\n**Quick sizing rule:** Choose a unit 3 feet longer and 2 feet wider than your bike's footprint. This ensures safe maneuvering and prevents damage.\n\n**Popular sizes by bike type:**\n* Sport bikes: 5x10 unit\n* Cruisers: 5x10 or 5x15 unit\n* Touring bikes: 5x15 or 10x10 unit\n* Multiple bikes: 10x15+ unit"
	},
	{
		question: "How do I prepare my motorcycle for winter storage?",
		answer: "**Essential steps (in order):**\n1. **Change oil and filter** - Prevents acid damage during storage\n2. **Fill tank + add fuel stabilizer** - Stops fuel degradation\n3. **Remove or maintain battery** - Use a battery tender or store indoors\n4. **Clean thoroughly** - Remove salt, dirt, debris\n5. **Apply protective wax** - Shields paint from moisture\n6. **Use a center stand** - Prevents tire flat spots\n\n**Pro tip:** Visit monthly to check the condition and start briefly if possible."
	},
	{
		question: "Can you store a motorcycle in self-storage?",
		answer: "Yes, absolutely. Most storage facilities welcome motorcycles and offer specific vehicle-friendly features like:\n* Wide drive-up access doors\n* Ground-floor units (no ramps/elevators)\n* Adequate ceiling height for windshields\n* Vehicle-specific insurance options\n\n**Common facility requirements:**\n* Drain fuel or use stabilizer\n* Remove battery (sometimes)\n* Maintain insurance coverage\n* No major repairs in the unit\n\n**Best practices:** Choose facilities with good lighting, security cameras, and controlled access for safe loading/unloading."
	},
	{
		question:
			"What security features should I look for in motorcycle storage?",
		answer: "**Must-have security features:**\n1. **Controlled gate access** with individual codes\n2. **Perimeter fencing** around the entire facility\n3. **Security cameras** with good coverage\n4. **Well-lit areas** for safe access\n5. **On-site management** or regular patrols\n\n**Bonus features:**\n* Individual unit alarms\n* Access logs and monitoring\n* Visible location (deters crime)\n* High-security lock options\n\n**Insurance tip:** Verify your motorcycle insurance covers theft from storage units. Consider GPS trackers for high-value bikes."
	},
	{
		question: "How much does motorcycle storage cost?",
		answer: "**Average monthly costs (2025):**\n* **5x10 unit:** $75-$125\n* **10x10 unit:** $110-$180\n* **Climate control:** Add $30-$60\n\n**Major cost factors:**\n* **Location:** Urban areas cost 30-50% more\n* **Season:** Spring rates are often 15% higher\n* **Security level:** Premium features add $10-25/month\n* **Access hours:** 24/7 access may include a small fee"
	}
];

const relatedContentCards = [
	{
		category: "Recreational Storage",
		url: "https://www.storage.com/blog/storing-a-motorcycle-self-storage/",
		date: "August 13, 2025",
		image: motorCycleStorage,
		content:
			"Your Ultimate Motorcycle Storage Solutions Guide: Keeping Your Bike Safe All Year"
	},
	{
		category: "Recreational Storage",
		url: "https://www.storage.com/blog/winterize-your-motorcycle-for-self-storage/",
		date: "January 8, 2025",
		image: motorCycleWinterize,
		content: "How to Winterize Your Motorcycle for Self Storage"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/garage-vs-storage-unit-5-benefits-of-self-storage/",
		date: "December 1, 2025",
		image: garageVsStorage,
		content: "Garage vs Storage Unit: 5 Benefits of Self Storage"
	}
];

const reviewsContent = {
	title: "Storage.com has a 4.4 out of 5 star rating on",
	testimonials: [
		{
			body: "My Storage.com representative was very helpful and patient with me, working through to find the best location and price.",
			author: "Jess",
			title: "Very helpful",
			imageSrc: FemaleAvatar
		},
		{
			body: "Storage.com did an excellent job helping me find a storage facility at a great price. Better than the price that I found.",
			author: "Alison",
			title: "Storage facility",
			imageSrc: AnnAlarcon
		},
		{
			body: "Storage.com found me a spot immediately for half the price I had found in my search. Very courteous representatives.",
			author: "Robert Fulton",
			title: "Excellent!",
			imageSrc: SMurphy
		}
	]
};

const motorcycleHerodescription = `
There’s no denying the immediate convenience of parking your bike outside — but you’re putting thousands of dollars and countless hours of joyriding on the table. The truth is that storing your motorcycle in a storage unit or other secure vehicle storage facility can reduce the rate of vehicle depreciation, mitigate the risk of theft, and even lower your insurance premium by up to 10%.\n\n
The [National Insurance Crime Bureau](https://www.nicb.org/news/news-releases/lock-your-bikes-motorcycle-thefts-rise-third-consecutive-year) even recommends that bike owners take the proper precautions when it comes to storing and protecting motorcycles. But there’s more to motorcycle storage than that. Going on an extended trip? Need a place to work on your bike? A dedicated storage unit is among the most practical motorcycle storage ideas.\n\n
Storage.com connects you with affordable motorcycle storage near me that costs less than your monthly bike payment but protects an investment you've worked hard to afford.
`;

export const PSPMotorcycleContent = {
	type: "Motorcycle",
	title: "Motorcycle Storage Units",
	sections: [
		{
			title: "Motorcycle Storage hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Motorcycle Storage Units",
						description: motorcycleHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find Motorcycle Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Motorcycle Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Motorcycle Storage Content",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Why Self Storage is Perfect for Your Motorcycle",
						background: "light-blue",
						listType: "single",
						content: [
							{
								id: "1",
								value: "**Protection from Weather & Theft**"
							},
							{
								id: "2",
								value: `Your motorcycle is an investment and a symbol of your thirst for excitement. Protect your investment and your adventure partner with proper storage. Storage units for motorcycles provide comprehensive protection from:`
							},
							{
								id: "3",
								value: "* **Weather damage:** UV rays, hail, rain, and snow that can fade paint and damage components.\n* **Temperature extremes:** Freezing temperatures that affect batteries and fluids, causing them to rapidly lose charge and lifespan.\n* **Theft and vandalism:** [Secure facilities with gated access](https://www.storage.com/blog/self-storage-security-features/), well-lit units, surveillance cameras, and more offer advanced protection.\n* **Accidental damage:** No more worrying about garage door dings, clumsy family members, strong gusts of wind, or duels over valuable driveway space."
							},
							{
								id: "4",
								value: "**Seasonal & Long-Term Solutions**\n\n"
							},
							{
								id: "5",
								value: `Depending on where you live, you might not have the luxury of year-round riding. Motorcycle storage facilities offer a variety of storage options for every season and need:`
							},
							{
								id: "6",
								value: "* Seasonal convenience: Easy access for spring tune-ups and fall winterization\n* Climate-controlled options for premium bikes and harsh climates\n* Security features, including gated access, surveillance, and individual unit alarms\n* More garage space for cars, tools, and family activities"
							},
							{
								id: "7",
								value: "**Flexible Storage Options**\n\n"
							},
							{
								id: "8",
								value: `Whether you're storing one bike or an entire collection, self-storage motorcycle solutions provide:\n* Various unit sizes to fit any motorcycle and budget\n* Short-term and long-term rental flexibility\n* Additional space for helmets, gear, and maintenance supplies\n* No long-term commitments or contracts`
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Motorcycle Storage Options",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Motorcycle Storage Options",
						background: "blue",
						markDown: optionsRichContent,
						textAlign: "left",
						additionalContent: true,
						mediaType: "video",
						mediaUrl: "6awlW9-E6Nw",
						videoTitle: "Motorcycle Storage Options"
					}
				}
			]
		},
		{
			title: "Motorcycle Storage Sizes",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "What Size Storage Unit for Motorcycle Storage?",
						background: "white",
						listType: "single",
						content: [
							{
								id: "1",
								value: "**The Basics: Motorcycle Storage at Every Size**\n\n"
							},
							{
								id: "2",
								value: `**5x5 Units:** Suitable only for very small bikes or scooters. Consider this the absolute minimum for compact storage.\n\n`
							},
							{
								id: "3",
								value: `**5x10 Units:** The most popular choice for single motorcycle storage. Perfect for most sport bikes, cruisers, and standard motorcycles with room for riding gear and basic maintenance supplies.`
							},
							{
								id: "4",
								value: "**10x10 Units:** Perfect for riders who want a dedicated motorcycle garage space. Room for bike plus workbench, tool storage, and comprehensive gear organization."
							},
							{
								id: "5",
								value: `**10x15+ Units:** Ultimate motorcycle storage rental solution for collectors or serious enthusiasts. Store multiple bikes, create a full workshop, and organize extensive gear collections.`
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Motorcycle Storage Size Comparison",
			noPadding: true,
			modules: [
				{
					type: "size-comparison",
					props: {
						title: "Storage Unit Size Guide for Motorcycles",
						background: "light-blue",
						description:
							"You know the basics on how to choose the right size storage unit for your motorcycle - but do you really know what's best? If you're not sure how your Harley or Indian Motorcycle fits in, here's a more detailed look at common storage unit sizes and what they can hold.",
						sizes: [
							{
								id: "5x10",
								label: "5x10",
								title: "5' Wide x 10' Long",
								description:
									"* 1 Sport Bike, like a Suzuki *GSX-R*, Yamaha *R1/R6*, Honda *CBR*, will fit in this unit\n* 1 Cruiser, like a Harley-Davidson *Sportster*, Indian *Scout*, Yamaha *Bolt* will also fit in this unit size\n* Room for some gear, tools, and spare parts"
							},
							{
								id: "5x15",
								label: "5x15",
								title: "5' Wide x 15' Long",
								description:
									"* Safely store a Touring Bike, such as a Honda *Gold Wing*, Harley *Road Glide*, BMW *R1250RT*\n* Adventure bikes (BMW *GS*, KTM *Adventure*, Honda *Africa Twin*) fit well into this unit size\n* Room for gear like saddlebags and other accessories"
							},
							{
								id: "10x10",
								label: "10x10",
								title: "10' Wide x 10' Long",
								description:
									"* Can hold a sport bike, cruiser, touring bike, or dual sport motorcycle\n* Room for protective gear and accessories\n* Additional amenities offer superior protection"
							},
							{
								id: "10x15",
								label: "10x15",
								title: "10' Wide x 15' Long",
								description:
									"* Holds 1-2 motorcycles in any bike style\n* Additional room for gear, maintenance supplies, and accessories"
							},
							{
								id: "10x20",
								label: "10x20",
								title: "10' Wide x 20' Long",
								description:
									"* Stores multiple motorcycles in any style\n* Room for accessory storage\n* Upgrade with electricity and climate control amenities for workshop space"
							}
						]
					}
				}
			]
		},
		{
			title: "Motorcycle Storage Costs",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						title: "Average Motorcycle Storage Costs",
						background: "white",
						description:
							"Motorcycle storage costs can vary depending on location, time of year, unit size, market demand, and other variable factors. Here's what you can expect your average monthly motorcycle storage costs to be:",
						storages: [
							{
								title: "5x10",
								bestFor: [
									"$65-$95/monthly budget",
									"Single motorcycle"
								]
							},
							{
								title: "10x10",
								bestFor: [
									"$95-$125/monthly budget",
									"Motorcycle & workshop"
								]
							},
							{
								title: "10x15",
								bestFor: [
									"$125-$200/monthly budget",
									"1+ motorcycles"
								]
							},
							{
								title: "10x20",
								bestFor: [
									"$140-$220/monthly budget",
									"Multiple motorcycles"
								]
							}
						]
					}
				}
			]
		},
		{
			title: "Cost Factors",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Cost Factors to Consider :",
						background: "blue",
						content: [
							{
								id: "1",
								value: "* **Location:** Urban areas cost 20-40% more than rural locations\n* **Security features:** Enhanced security may add $10-25 monthly\n* **Seasonal demand:** Spring rates are often 10-15% higher than winter rates\n* **Access hours:** 24/7 access may include a small premium"
							},
							{
								id: "2",
								value: "**Money-Saving Tips:**\n* Book motorcycle winter storage during peak summer riding season\n* Consider sharing larger units with fellow riders\n* Look for first-month-free or move-in specials\n* Choose standard units over climate control in moderate climates"
							}
						],
						isColumnWise: false,
						listType: "single"
					}
				}
			]
		},
		{
			title: "Prepare Your Motorcycle for Storage",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "How to Prepare Your Motorcycle for Storage",
						background: "blue",
						description: "",
						textAlign: "left",
						additionalContent: true,
						markDown: prepareMotorcycleRichContent,
						mediaType: "image",
						mediaUrl: prepareMotorcycle,
						videoTitle: ""
					}
				}
			]
		},
		{
			title: "Motorcycle Storage CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Motorcycle",
						background: "white",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: inlineCTA,
						imageAltText: "Find motorcycle storage"
					}
				}
			]
		},
		{
			title: "Motorcycle Customer Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: { ...reviewsContent, background: "blue" }
				}
			]
		},
		{
			title: "Motorcycle Storage FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Motorcycle Storage FAQs",
						description: "",
						faqs
					}
				}
			]
		},
		{
			title: "Motorcycle Storage Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About Motorcycle Storage",
						background: "light-blue",
						unitCards: relatedContentCards
					}
				}
			]
		},
		{
			title: "Motorcycle Links to other PSP",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: {
							title: "More Ways to Store",
							image: illustrationSearch,
							storageTypes: storageCategories.motorcycle
						}
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						storageType: "motorcycle",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPMotorcycleContent;
